<script setup lang="ts">
import { Roles } from '@/constants'
import type { UserModel } from '@/services/http/httpResponses'
import usersHttpService from '@/services/http/usersHttpService'
import { computed, ref } from 'vue'
import { z } from 'zod'
import { zodResolver } from '@primevue/forms/resolvers/zod'
import { useToast } from 'primevue/usetoast'
import type { Id } from '@raildev/shared-utils'

definePage({
  meta: { allowedRoles: [Roles.Admin] },
})

const toast = useToast()
const users = ref<UserModel[]>([])

const refreshUsers = async () => {
  const res = await usersHttpService.getUsers({ role: Roles.Agent })
  users.value = res.users
}

const isLocked = (lockoutEnd: string | null) =>
  lockoutEnd != null && new Date(lockoutEnd) > new Date()

const toggleLockout = async (row: { id: Id; locked: boolean }) => {
  try {
    await usersHttpService.setUserLockout(row.id, !row.locked)
    await refreshUsers()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось изменить статус блокировки',
      life: 3000,
    })
  }
}

const formattedUsers = computed(() =>
  users.value.map((u) => ({
    id: u.id,
    name: u.name ?? '-',
    email: u.email ?? '-',
    createdAt: u.createdAt ? new Date(u.createdAt).toLocaleDateString('ru-RU') : '-',
    activated: u.emailConfirmed || u.phoneNumberConfirmed,
    locked: isLocked(u.lockoutEnd),
  })),
)

const showEditDialog = ref(false)
const selectedUser = ref<UserModel | null>(null)

const editSchema = z.object({
  email: z.email({ message: 'Введите корректный email' }),
  name: z.string().nullable().optional(),
})
const editResolver = zodResolver(editSchema)

const passwordRules = z
  .string()
  .min(6, { message: 'Пароль должен содержать минимум 6 символов' })
  .refine((v) => /[a-z]/.test(v), {
    message: 'Пароль должен содержать хотя бы одну строчную букву',
  })
  .refine((v) => /[A-Z]/.test(v), {
    message: 'Пароль должен содержать хотя бы одну заглавную букву',
  })
  .refine((v) => /\d+/.test(v), { message: 'Пароль должен содержать хотя бы одну цифру' })
  .refine((v) => /\W/.test(v), {
    message: 'Пароль должен содержать хотя бы один специальный символ',
  })

const createSchema = z
  .object({
    email: z.email({ message: 'Введите корректный email' }),
    name: z.string().optional(),
    password: passwordRules,
    confirmPassword: z.string().min(1, { message: 'Повторите пароль' }),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Пароли не совпадают',
    path: ['confirmPassword'],
  })

const createResolver = zodResolver(createSchema)
const showCreateDialog = ref(false)
const createInitialValues = { email: '', name: '', password: '', confirmPassword: '' }

const createAgent = async ({
  valid,
  values,
}: {
  valid: boolean
  values: z.infer<typeof createSchema>
}) => {
  if (!valid) return
  try {
    await usersHttpService.createUser(
      values.email,
      values.password,
      Roles.Agent,
      (values.name ?? '').trim() || null,
    )
    toast.add({ severity: 'success', summary: 'Успех', detail: 'Агент создан', life: 3000 })
    showCreateDialog.value = false
    await refreshUsers()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось создать агента. Возможно, email уже занят.',
      life: 3000,
    })
  }
}

const editInitialValues = computed(() => ({
  email: selectedUser.value?.email ?? '',
  name: selectedUser.value?.name ?? '',
}))

const openEditDialog = (row: { id: Id }) => {
  selectedUser.value = users.value.find((x) => x.id === row.id) ?? null
  showEditDialog.value = true
}

const saveEdit = async ({
  valid,
  values,
}: {
  valid: boolean
  values: { email: string; name?: string | null }
}) => {
  if (!valid || selectedUser.value == null) return

  try {
    await usersHttpService.updateUserDetails(
      selectedUser.value.id,
      values.email,
      (values.name ?? '').trim() || null,
    )
    toast.add({ severity: 'success', summary: 'Успех', detail: 'Данные обновлены', life: 3000 })
    showEditDialog.value = false
    await refreshUsers()
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Ошибка',
      detail: 'Не удалось обновить данные',
      life: 3000,
    })
  }
}

await refreshUsers()
</script>

<template>
  <section class="flex flex-col w-full">
    <Toast />
    <div class="flex justify-end mb-4">
      <Button label="Добавить агента" @click="showCreateDialog = true" />
    </div>

    <DataTable :value="formattedUsers" dataKey="id">
      <Column field="name" header="Имя" />
      <Column field="email" header="Email" />
      <Column field="createdAt" header="Дата регистрации" />
      <Column header="Статус активации">
        <template #body="slotProps">
          <Tag
            :value="slotProps.data.activated ? 'Активирован' : 'Не активирован'"
            :severity="slotProps.data.activated ? 'success' : 'warn'"
          />
        </template>
      </Column>
      <Column header="Статус">
        <template #body="slotProps">
          <Tag
            :value="slotProps.data.locked ? 'Заблокирован' : 'Активен'"
            :severity="slotProps.data.locked ? 'danger' : 'success'"
          />
        </template>
      </Column>
      <Column header="Действия">
        <template #body="slotProps">
          <div class="flex gap-2">
            <Button label="Редактировать" @click="openEditDialog(slotProps.data)" />
            <Button
              :label="slotProps.data.locked ? 'Разблокировать' : 'Заблокировать'"
              :severity="slotProps.data.locked ? 'success' : 'danger'"
              @click="toggleLockout(slotProps.data)"
            />
          </div>
        </template>
      </Column>
      <template #empty>
        <div class="flex flex-col gap-4 items-center">
          <span class="pi pi-search text-6xl" />
          <h3 class="font-medium">Агенты не найдены</h3>
        </div>
      </template>
    </DataTable>

    <Dialog
      v-model:visible="showCreateDialog"
      header="Создать агента"
      :modal="true"
      :class="'w-[90vw] sm:w-[600px]'"
      :closable="true"
    >
      <Form
        v-slot="$form"
        :resolver="createResolver"
        :initialValues="createInitialValues"
        @submit="createAgent"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <label for="createEmail">Email</label>
          <InputText id="createEmail" name="email" type="email" class="w-full" />
          <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">
            {{ $form.email?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createName">Имя (опционально)</label>
          <InputText id="createName" name="name" class="w-full" />
        </div>
        <div class="flex flex-col gap-1">
          <label for="createPassword">Пароль</label>
          <Password
            id="createPassword"
            name="password"
            class="w-full"
            :feedback="true"
            fluid
            toggle-mask
          />
          <Message v-if="$form.password?.invalid" severity="error" size="small" variant="simple">
            {{ $form.password?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="createConfirmPassword">Повторите пароль</label>
          <Password
            id="createConfirmPassword"
            name="confirmPassword"
            class="w-full"
            :feedback="false"
            fluid
            toggle-mask
          />
          <Message
            v-if="$form.confirmPassword?.invalid"
            severity="error"
            size="small"
            variant="simple"
          >
            {{ $form.confirmPassword?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showCreateDialog = false" />
          <Button class="w-full" type="submit" label="Создать" />
        </div>
      </Form>
    </Dialog>

    <Dialog
      v-model:visible="showEditDialog"
      header="Редактировать агента"
      :modal="true"
      :class="'w-[90vw] sm:w-[600px]'"
      :closable="true"
    >
      <Form
        v-slot="$form"
        :resolver="editResolver"
        :initialValues="editInitialValues"
        @submit="saveEdit"
        class="flex flex-col gap-4"
      >
        <div class="flex flex-col gap-1">
          <label for="editEmail">Email</label>
          <InputText id="editEmail" name="email" type="email" class="w-full" />
          <Message v-if="$form.email?.invalid" severity="error" size="small" variant="simple">
            {{ $form.email?.error?.message }}
          </Message>
        </div>
        <div class="flex flex-col gap-1">
          <label for="editName">Имя</label>
          <InputText id="editName" name="name" class="w-full" />
          <Message v-if="$form.name?.invalid" severity="error" size="small" variant="simple">
            {{ $form.name?.error?.message }}
          </Message>
        </div>
        <div class="flex gap-2">
          <Button class="w-full" label="Отмена" text @click="showEditDialog = false" />
          <Button class="w-full" type="submit" label="Сохранить" />
        </div>
      </Form>
    </Dialog>
  </section>
</template>

<style scoped></style>
